import { useEffect, useId, useRef, useState } from 'react';
import SafeImage from '../../components/SafeImage';
import {
  MEMORY_SHARE_JOURNEY_DISABLED_NOTE,
  MEMORY_SHARE_POST_KINDS,
  MEMORY_SHARE_PRIVATE_NOTE,
  MEMORY_SHARE_SNAPSHOT_NOTE,
  buildPublishMemorySocialPayload,
  createShareClientRequestId,
  emptyShareDraft,
  memoryShareErrorMessage,
  memorySharePostKindLabel,
  shareCoverPreviewFromMemory,
  validateShareDraft,
  classifyMemoryShareError,
  normalizeMemorySocialPublication,
} from './memoryShareModel';
import { memorySocialPublicationApi } from '../../api/memorySocialPublicationApi';

/**
 * Memory → Social share compose (modal). Edits publication snapshot only — never patches Memory.
 */
export default function MemoryShareComposer({
  open,
  entry,
  journeyId,
  journeyPublicationId,
  publicationProbePending = false,
  onClose,
  onPublished,
}) {
  const titleId = useId();
  const descId = useId();
  const textId = useId();
  const errorId = useId();
  const publishing = useRef(false);
  const clientRequestIdRef = useRef(null);
  const disposed = useRef(false);
  const [draft, setDraft] = useState(() => emptyShareDraft(entry));
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    disposed.current = false;
    return () => {
      disposed.current = true;
    };
  }, []);

  useEffect(() => {
    if (!open || !entry) return;
    publishing.current = false;
    clientRequestIdRef.current = null;
    setDraft(emptyShareDraft(entry));
    setError('');
    setBusy(false);
  }, [open, entry]);

  if (!open || !entry) return null;

  const cover = shareCoverPreviewFromMemory(entry);
  const journeyEnabled = Boolean(journeyPublicationId);
  const shareLabel = busy ? '分享中…' : '發布動態';

  function onPostKindChange(nextKind) {
    if (nextKind === 'JOURNEY' && !journeyEnabled) return;
    setDraft((current) => ({ ...current, postKind: nextKind }));
    setError('');
  }

  async function onSubmit(event) {
    event.preventDefault();
    if (publishing.current || busy) return;
    const validation = validateShareDraft(draft, { journeyPublicationId });
    if (validation) {
      setError(validation);
      return;
    }
    if (!clientRequestIdRef.current) {
      clientRequestIdRef.current = createShareClientRequestId();
    }
    publishing.current = true;
    setBusy(true);
    setError('');
    try {
      const payload = buildPublishMemorySocialPayload(draft, {
        clientRequestId: clientRequestIdRef.current,
        journeyPublicationId,
      });
      const raw = await memorySocialPublicationApi.publish(
        journeyId,
        entry.memoryEntryId,
        payload,
      );
      if (disposed.current) return;
      const result = normalizeMemorySocialPublication(raw);
      clientRequestIdRef.current = null;
      if (onPublished) onPublished(result);
    } catch (cause) {
      if (disposed.current) return;
      const kind = classifyMemoryShareError(cause);
      setError(memoryShareErrorMessage(kind, cause?.raw?.message || cause?.message));
      if (kind === 'idempotency-conflict') {
        clientRequestIdRef.current = createShareClientRequestId();
      }
    } finally {
      publishing.current = false;
      if (!disposed.current) setBusy(false);
    }
  }

  return (
    <div className="journey-journal-share" role="presentation">
      <button
        type="button"
        className="journey-journal-share__backdrop"
        aria-label="關閉分享"
        disabled={busy}
        onClick={() => { if (!busy) onClose?.(); }}
      />
      <div
        className="journey-journal-share__dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={descId}
      >
        <header className="journey-journal-share__header">
          <h3 id={titleId}>分享到動態</h3>
          <p id={descId} className="journey-journal-share__lede">
            {MEMORY_SHARE_SNAPSHOT_NOTE}
          </p>
          {entry.isPrivate ? (
            <p className="journey-journal-share__privacy" role="status">
              {MEMORY_SHARE_PRIVATE_NOTE}
            </p>
          ) : null}
        </header>

        <section className="journey-journal-share__source" aria-label="原日記預覽">
          <h4>{entry.title || entry.memoryTypeLabel}</h4>
          <p className="journey-journal-share__source-meta">
            <span>{entry.visibilityLabel}</span>
            <span>{entry.memoryTypeLabel}</span>
          </p>
          {entry.contentPreview ? (
            <p className="journey-journal-share__source-body">{entry.contentPreview}</p>
          ) : null}
        </section>

        {cover.hasCover ? (
          <figure className="journey-journal-share__cover">
            <SafeImage
              className="journey-journal-share__cover-img"
              src={cover.url}
              alt="動態封面預覽"
            />
            <figcaption>動態封面（依日記第一張圖片）</figcaption>
          </figure>
        ) : (
          <p className="journey-journal-share__cover-empty" role="status">此日記沒有圖片，動態將不帶封面。</p>
        )}

        <form className="journey-journal-share__form" onSubmit={onSubmit} noValidate>
          <fieldset className="journey-journal__fieldset">
            <legend>動態類型</legend>
            {MEMORY_SHARE_POST_KINDS.map((kind) => {
              const disabled = kind === 'JOURNEY' && !journeyEnabled;
              return (
                <label key={kind} className="journey-journal__radio">
                  <input
                    type="radio"
                    name="share-post-kind"
                    value={kind}
                    checked={draft.postKind === kind}
                    disabled={busy || disabled || publicationProbePending}
                    onChange={() => onPostKindChange(kind)}
                  />
                  <span>
                    {memorySharePostKindLabel(kind)}
                    {disabled ? (
                      <span className="journey-journal-share__kind-hint">
                        {' '}
                        — {MEMORY_SHARE_JOURNEY_DISABLED_NOTE}
                      </span>
                    ) : null}
                  </span>
                </label>
              );
            })}
          </fieldset>

          <label htmlFor={textId}>
            動態內容
            <textarea
              id={textId}
              value={draft.textContent}
              disabled={busy}
              maxLength={4000}
              rows={6}
              onChange={(event) => {
                setDraft((current) => ({ ...current, textContent: event.target.value }));
                setError('');
              }}
            />
          </label>
          <p className="journey-journal-share__hint">
            這裡的修改只會寫入這次動態快照，不會變更原日記。
          </p>

          {(draft.postKind === 'MOOD' || draft.postKind === 'FOOD') && journeyEnabled ? (
            <label className="journey-journal__radio">
              <input
                type="checkbox"
                checked={Boolean(draft.attachJourney)}
                disabled={busy}
                data-testid="memory-share-attach-journey"
                onChange={(event) => {
                  setDraft((current) => ({
                    ...current,
                    attachJourney: event.target.checked,
                  }));
                  setError('');
                }}
              />
              <span>一併附上此行程（選填）</span>
            </label>
          ) : null}

          {error ? (
            <p className="journey-journal__error" id={errorId} role="alert">{error}</p>
          ) : null}

          <div className="journey-journal-share__actions">
            <button type="button" disabled={busy} onClick={() => onClose?.()}>
              取消
            </button>
            <button type="submit" disabled={busy || publicationProbePending}>
              {shareLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
